import React, { useEffect, useState } from 'react';
import {
  ActionDrawerItems,
  ActionMenu,
  Alert,
  Divider,
  Drawer,
  DrawerList,
  Icon,
  Input,
  Pills,
  Text,
  Toggle,
} from 'barte-design-system';
import { ArrowClockwise, CaretDown, Prohibit } from '@phosphor-icons/react';
import { BASE_ROLE, ROLE_ICON, ROLES, type Role, type User, type UserStatus } from './roles';

export type StatusAction = 'inativar' | 'reativar' | 'reenviar';

export type DrawerMode = 'create' | 'view' | 'edit';

interface DrawerUsuarioProps {
  isOpen: boolean;
  mode: DrawerMode;
  user: User | null;
  onClose: () => void;
  onCreate: (data: { email: string; roleIds: string[] }) => void;
  onSave: (userId: string, data: { email: string; roleIds: string[] }) => void;
  onStatusAction: (action: StatusAction, user: User) => void;
}

const STATUS_PILL: Record<UserStatus, 'success' | 'warning' | 'error' | 'default'> = {
  Ativo: 'success',
  Pendente: 'warning',
  Expirado: 'error',
  Inativo: 'default',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Linha de perfil (ícone + descrição + toggle) ───────────────────────────────

function RoleRow({
  role,
  editable,
  selected,
  disabled,
  onToggle,
}: {
  role: Role;
  editable: boolean;
  selected: boolean;
  disabled?: boolean;
  onToggle: (roleId: string) => void;
}) {
  const clickable = editable && !disabled;
  const cls = [
    'up-perm-row',
    editable && selected ? 'up-perm-row--active' : '',
    clickable ? 'up-perm-row--clickable' : '',
    !editable ? 'up-perm-row--readonly' : '',
    disabled && !selected ? 'up-perm-row--muted' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cls}
      onClick={() => {
        if (clickable) onToggle(role.id);
      }}
    >
      <span className={`up-perm-icon${selected ? ' up-perm-icon--on' : ''}`}>
        <Icon icon={ROLE_ICON[role.id]} size={20} weight="regular" />
      </span>

      <div className="up-perm-text">
        <div className="up-perm-titleline">
          <Text variant="label-sm">{role.name}</Text>
          {role.id === 'admin' && <span className="up-perm-tag">Todas as permissões</span>}
        </div>
        <Text variant="body-xs" className="up-role-desc">{role.description}</Text>
      </div>

      {editable && <Toggle checked={selected} disabled={disabled} onChange={() => {}} />}
    </div>
  );
}

// ─── Lista de perfis (leitura ou edição) ────────────────────────────────────────

function RoleList({
  editable,
  selectedRoleIds,
  userRoleIds,
  onToggle,
}: {
  editable: boolean;
  selectedRoleIds: string[];
  userRoleIds: string[];
  onToggle: (roleId: string) => void;
}) {
  // Ordem: Admin primeiro, depois Visualização (base) e os demais perfis.
  const admin = ROLES.find((r) => r.id === 'admin');
  const others = ROLES.filter((r) => r.id !== 'admin');
  const allRoles = [admin, BASE_ROLE, ...others].filter(Boolean) as Role[];
  const rolesToShow = editable ? allRoles : allRoles.filter((r) => userRoleIds.includes(r.id));
  const adminOn = selectedRoleIds.includes('admin');
  const hasOther = selectedRoleIds.some((id) => id !== 'admin' && id !== BASE_ROLE.id);

  return (
    <div className="up-perm-rows">
      {rolesToShow.map((role) => {
        const isBase = role.id === BASE_ROLE.id;
        // Admin ligado → tudo desabilitado. Base fica travado (obrigatório) quando há outro perfil.
        const disabled = role.id === 'admin' ? false : adminOn || (isBase && hasOther);
        return (
          <RoleRow
            key={role.id}
            role={role}
            editable={editable}
            selected={selectedRoleIds.includes(role.id)}
            disabled={disabled}
            onToggle={onToggle}
          />
        );
      })}
    </div>
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────────────────

export function DrawerUsuario({ isOpen, mode, user, onClose, onCreate, onSave, onStatusAction }: DrawerUsuarioProps) {
  const [internalMode, setInternalMode] = useState<DrawerMode>(mode);
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  // Guarda a seleção anterior antes de ligar o Admin (para restaurar ao desligar)
  const [prevRoleIds, setPrevRoleIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setInternalMode(mode);
    setError(null);
    if (mode === 'create') {
      setEmail('');
      setRoleIds([]);
      setPrevRoleIds([]);
    } else if (user) {
      setEmail(user.email);
      setRoleIds(user.roleIds);
      setPrevRoleIds(user.roleIds.filter((r) => r !== 'admin'));
    }
  }, [isOpen, mode, user]);

  function toggleRole(roleId: string) {
    if (roleId === 'admin') {
      if (roleIds.includes('admin')) {
        // Desliga Admin → restaura o estado anterior
        setRoleIds(prevRoleIds);
      } else {
        // Liga Admin → guarda o estado atual e deixa só o Admin (demais ficam desabilitados)
        setPrevRoleIds(roleIds.filter((r) => r !== 'admin'));
        setRoleIds(['admin']);
      }
      return;
    }
    // Demais perfis (só alcançável com Admin desligado, pois ficam disabled quando ligado)
    setRoleIds((prev) => {
      let next = prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId];
      // Visualização é o perfil básico: se há qualquer outro perfil ligado, garante o base
      const hasOther = next.some((id) => id !== 'admin' && id !== BASE_ROLE.id);
      if (hasOther && !next.includes(BASE_ROLE.id)) next = [...next, BASE_ROLE.id];
      return next;
    });
  }

  function validate(): boolean {
    if (!EMAIL_RE.test(email.trim())) {
      setError('Informe um email válido.');
      return false;
    }
    setError(null);
    return true;
  }

  function handlePrimary() {
    if (internalMode === 'view') {
      setInternalMode('edit');
      return;
    }
    if (!validate()) return;
    const data = { email: email.trim(), roleIds };
    if (internalMode === 'create') {
      onCreate(data);
    } else if (user) {
      onSave(user.id, data);
      setInternalMode('view');
    }
  }

  function handleSecondary() {
    if (internalMode === 'edit' && user) {
      setEmail(user.email);
      setRoleIds(user.roleIds);
      setError(null);
      setInternalMode('view');
    } else {
      onClose();
    }
  }

  const title =
    internalMode === 'create' ? 'Convidar usuário' : internalMode === 'edit' ? 'Editar usuário' : 'Detalhes do usuário';

  const footer =
    internalMode === 'view'
      ? { primaryButton: { children: 'Editar', onClick: handlePrimary } }
      : {
          primaryButton: {
            children: internalMode === 'create' ? 'Enviar convite' : 'Salvar',
            onClick: handlePrimary,
            disabled: !email.trim(),
          },
          secondaryButton: { children: 'Cancelar', onClick: handleSecondary },
        };

  function buildStatusActions(u: User) {
    if (u.status === 'Ativo') {
      return [{ label: 'Desativar', variant: 'icon' as const, icon: <Icon icon={Prohibit} size={20} weight="regular" />, onClick: () => onStatusAction('inativar', u) }];
    }
    if (u.status === 'Inativo') {
      return [{ label: 'Reativar', variant: 'icon' as const, icon: <Icon icon={ArrowClockwise} size={20} weight="regular" />, onClick: () => onStatusAction('reativar', u) }];
    }
    return [{ label: 'Reenviar convite', variant: 'icon' as const, icon: <Icon icon={ArrowClockwise} size={20} weight="regular" />, onClick: () => onStatusAction('reenviar', u) }];
  }

  const isCreate = internalMode === 'create';
  const isEditable = isCreate || internalMode === 'edit';
  // Remonta a lista de perfis ao alternar leitura/edição.
  const roleListKey = `${internalMode}-${user?.id ?? 'new'}`;

  return (
    <Drawer isOpen={isOpen} title={title} onClose={onClose} footer={footer}>
      <div className="up-drawer-body">
        {error && (
          <Alert variant="error" title="Não foi possível continuar" description={error} showCloseButton={false} />
        )}

        {!isCreate && user && (
          <>
            <div className="up-account-meta">
              <Pills label={`Último acesso: ${user.lastAccess}`} state="default" variant="light" size="sm" />
              <ActionMenu
                align="right"
                trigger={
                  <button type="button" className={`up-status-trigger up-status up-status--${user.status.toLowerCase()}`}>
                    <Pills
                      label={user.status}
                      state={STATUS_PILL[user.status]}
                      variant={user.status === 'Inativo' ? 'light' : 'fill'}
                      icon={CaretDown}
                      size="sm"
                    />
                  </button>
                }
              >
                <DrawerList>
                  <ActionDrawerItems items={buildStatusActions(user)} />
                </DrawerList>
              </ActionMenu>
            </div>
            <Divider />
          </>
        )}

        <Input
          label="Email"
          placeholder="nome@empresa.com.br"
          value={email}
          disabled={!isEditable}
          error={error && !EMAIL_RE.test(email.trim()) ? true : undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmail(e.target.value)}
        />

        <div className="up-role-selector">
          <Text variant="label-md">Permissões de acesso</Text>
          <RoleList
            key={roleListKey}
            editable={isEditable}
            selectedRoleIds={roleIds}
            userRoleIds={user?.roleIds ?? []}
            onToggle={toggleRole}
          />
        </div>
      </div>
    </Drawer>
  );
}
